import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DemoBanner from "@/components/DemoBanner";
import {
  ShieldAlert, Search, Ban, RotateCcw, Flag, Wifi, MessageSquare, Users2,
  Radio, Eye, Trash2, Check, X, PlusCircle, ExternalLink, ChevronRight, LifeBuoy, DollarSign, Sparkles,
  ShieldCheck,
} from "lucide-react";

const TABS = [
  { key: "accounts", label: "Accounts", icon: Users2 },
  { key: "reports", label: "Reports", icon: Flag },
  { key: "ipbans", label: "IP Bans", icon: Wifi },
  { key: "live", label: "Live Monitor", icon: Radio },
];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("accounts");

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (!user.is_admin && !user.is_demo) { nav("/"); return; }
  }, [user, nav]);

  if (!user?.is_admin && !user?.is_demo) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-root">
      <DemoBanner />      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)" }}>
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Platform Admin</div>
          <h1 className="sc-heading text-3xl font-black tracking-tight">Trust &amp; Safety Console</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-white/10 pb-2 items-center">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-t flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition ${
              tab === t.key
                ? "bg-[color:var(--sc-volt)] text-black"
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
            data-testid={`admin-tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
        <Link
          to="/admin/support"
          className="ml-auto px-4 py-2 rounded flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition text-[color:var(--sc-cyan)] hover:bg-[color:var(--sc-cyan)]/10 border border-[color:var(--sc-cyan)]/30"
          data-testid="admin-support-link"
        >
          <LifeBuoy className="w-3.5 h-3.5" /> Support Tickets <ChevronRight className="w-3 h-3" />
        </Link>
        <Link
          to="/admin/finance"
          className="px-4 py-2 rounded flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition text-[color:var(--sc-volt)] hover:bg-[color:var(--sc-volt)]/10 border border-[color:var(--sc-volt)]/30"
          data-testid="admin-finance-link"
        >
          <DollarSign className="w-3.5 h-3.5" /> Finance <ChevronRight className="w-3 h-3" />
        </Link>
        <Link
          to="/admin/rbac"
          className="px-4 py-2 rounded flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition text-[#00F0FF] hover:bg-[#00F0FF]/10 border border-[#00F0FF]/30"
          data-testid="admin-rbac-link"
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Access Control <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {tab === "accounts" && <AccountsTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "ipbans" && <IPBansTab />}
      {tab === "live" && <LiveMonitorTab />}
    </div>
  );
}

// ============================================================
// ACCOUNTS TAB — search users, ban/unban, drill-down
// ============================================================
function AccountsTab() {
  const [users, setUsers] = useState([]);
  const [banned, setBanned] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const [u, b] = await Promise.all([
      api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      api.get("/admin/users/banned"),
    ]);
    setUsers(u.data); setBanned(b.data);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const doBan = async () => {
    if (!target) return;
    try {
      await api.post(`/admin/users/${target.username}/ban`, { username: target.username, reason });
      toast.success(`@${target.username} banned`);
      setTarget(null); setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ban failed"); }
  };
  const doUnban = async (username) => {
    try { await api.post(`/admin/users/${username}/unban`); toast.success("Unbanned"); load(); } catch { toast.error("Failed"); }
  };

  const doDelete = async (u) => {
    const typed = window.prompt(
      `⚠️ HARD-DELETE @${u.username}?\n\nThis erases their user row plus every channel, DM, tip, subscription, LFG post, VOD, and score they own. It CANNOT be undone.\n\nType the username exactly to confirm:`
    );
    if (!typed) return;
    if (typed !== u.username) {
      toast.error("Username didn't match — nothing was deleted.");
      return;
    }
    try {
      const r = await api.delete(`/admin/users/${u.username}`);
      const cascade = r.data?.cascade || {};
      const rowCount = Object.values(cascade).reduce((a, b) => a + (b || 0), 0);
      toast.success(`@${u.username} deleted`, {
        description: `${rowCount} related rows removed.`,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const openDetail = async (u) => {
    try { const r = await api.get(`/admin/user/${u.username}`); setDetail(r.data); }
    catch { toast.error("Failed to load detail"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="sc-card p-6">
        <div className="sc-heading text-lg font-bold mb-3">All Users</div>
        <div className="relative mb-4">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search username" className="sc-input !pl-9 text-sm" data-testid="admin-search" />
        </div>
        <div className="max-h-[500px] overflow-y-auto space-y-1">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-2 border border-white/5 rounded-sm hover:border-white/20 transition">
              <button onClick={() => openDetail(u)} className="flex-1 min-w-0 text-left" data-testid={`admin-user-${u.username}`}>
                <div className="text-sm font-semibold flex items-center gap-2">
                  {u.username}
                  {u.is_admin && <span className="chip-neon cyan text-[9px]">ADMIN</span>}
                  {u.is_verified_streamer && <span className="chip-neon volt text-[9px]">VERIFIED</span>}
                  {u.banned && <span className="chip-neon pink text-[9px]">BANNED</span>}
                </div>
                <div className="text-[10px] text-zinc-500 flex items-center gap-1 flex-wrap">{u.email} · {u.tier} · {u.gold_coins ?? 0} GC · {u.sparks ?? 0}<Sparkles className="w-2.5 h-2.5" /></div>
              </button>
              {!u.banned && !u.is_admin && (
                <button onClick={() => setTarget(u)} className="sc-btn-ghost text-xs flex items-center gap-1" data-testid={`admin-ban-${u.username}`}>
                  <Ban className="w-3 h-3" /> Ban
                </button>
              )}
              {u.banned && (
                <button onClick={() => doUnban(u.username)} className="sc-btn-ghost text-xs flex items-center gap-1" data-testid={`admin-unban-${u.username}`}>
                  <RotateCcw className="w-3 h-3" /> Unban
                </button>
              )}
              {!u.is_admin && u.username.toLowerCase() !== "developer" && (
                <button
                  onClick={() => doDelete(u)}
                  className="ml-1 p-1.5 rounded-sm border border-white/10 text-zinc-500 hover:border-[color:var(--sc-pink)] hover:text-[color:var(--sc-pink)]"
                  data-testid={`admin-delete-${u.username}`}
                  title="Permanently delete this account (irreversible)"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {users.length === 0 && <div className="text-xs text-zinc-500 py-4 text-center">No users match.</div>}
        </div>
      </div>

      <div className="sc-card p-6">
        <div className="sc-heading text-lg font-bold mb-3">Banned Accounts ({banned.length})</div>
        <div className="max-h-[500px] overflow-y-auto space-y-1">
          {banned.length === 0 && <div className="text-xs text-zinc-500 py-4 text-center">No banned accounts.</div>}
          {banned.map((u) => (
            <div key={u.id} className="p-3 border border-[color:var(--sc-pink)]/30 bg-[color:var(--sc-pink)]/5 rounded-sm">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold">{u.username}</div>
                <button onClick={() => doUnban(u.username)} className="sc-btn text-[10px] px-3 py-1 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Restore</button>
              </div>
              <div className="text-[10px] text-zinc-400">{u.email}</div>
              {u.banned_reason && <div className="text-[11px] text-zinc-300 mt-1">&quot;{u.banned_reason}&quot;</div>}
              <div className="text-[10px] text-zinc-500 mt-1">by {u.banned_by} · {u.banned_at ? new Date(u.banned_at).toLocaleString() : ""}</div>
            </div>
          ))}
        </div>
      </div>

      {target && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(20px)" }} onClick={() => setTarget(null)}>
          <div className="glass max-w-md w-full p-6 rounded-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sc-label mb-1" style={{ color: "var(--sc-pink)" }}>Ban Account</div>
            <h2 className="sc-heading text-2xl font-bold mb-4">Ban @{target.username}?</h2>
            <p className="text-sm text-zinc-400 mb-4">This suspends the account platform-wide.</p>
            <label className="sc-label block mb-2">Reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="sc-input min-h-[80px] mb-3" placeholder="Terms violation, harassment, etc." data-testid="admin-ban-reason" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setTarget(null)} className="sc-btn-ghost text-xs">Cancel</button>
              <button onClick={doBan} className="sc-btn text-xs" style={{ background: "#FF1053" }} data-testid="admin-ban-confirm">
                <Ban className="w-3 h-3 inline mr-1" /> Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && <UserDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function UserDetailModal({ detail, onClose }) {
  const u = detail.user; const s = detail.stats;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(20px)" }} onClick={onClose}>
      <div className="sc-card p-6 max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-user-detail">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-2xl font-black">@{u.username}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-zinc-500 sc-mono mb-4">{u.email} · {u.user_code} · joined {u.created_at?.slice(0,10)}</div>
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <Stat label="Reports against" value={s.reports_against} tone="pink" />
          <Stat label="Channel bans" value={s.channels_banned_from} tone="orange" />
          <Stat label="Channel mutes" value={s.channels_muted_on} tone="cyan" />
          <Stat label="Posts" value={s.posts_count} tone="volt" />
        </div>
        {detail.last_seen_ips?.length > 0 && (
          <div>
            <div className="sc-label mb-2">Recent IPs</div>
            <div className="space-y-1">
              {detail.last_seen_ips.map((ip, i) => (
                <div key={i} className="text-xs sc-mono flex items-center justify-between p-2 bg-black/30 rounded">
                  <span>{ip.ip}</span>
                  <span className="text-zinc-500">{ip.at?.slice(0, 16)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Link to={`/u/${u.username}`} className="sc-btn-ghost text-xs mt-4 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View profile</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded border border-white/5 p-2 bg-black/30">
      <div className="sc-label text-[9px]" style={{ color: `var(--sc-${tone})` }}>{label}</div>
      <div className="text-xl sc-heading font-black text-white">{value}</div>
    </div>
  );
}

// ============================================================
// REPORTS TAB
// ============================================================
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reports?status=${status}&limit=200`);
      setReports(r.data);
    } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const setReportStatus = async (id, newStatus, notes = "") => {
    try {
      await api.patch(`/admin/reports/${id}`, { status: newStatus, notes });
      toast.success(`Report ${newStatus}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="sc-card p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="sc-heading text-lg font-bold">Reports Queue</div>
        <div className="flex gap-1">
          {["open", "reviewing", "actioned", "dismissed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs rounded uppercase tracking-widest font-bold ${
                status === s ? "bg-[color:var(--sc-cyan)] text-black" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-report-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {loading && <div className="text-xs text-zinc-500 py-2">loading…</div>}
      {reports.length === 0 && !loading && <div className="text-xs text-zinc-500 py-8 text-center">No reports in this bucket.</div>}
      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.id} className="rounded-md border border-white/10 p-4" data-testid={`report-${r.id}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="sc-heading font-bold">@{r.reporter_username}</span>
                  <ChevronRight className="w-3 h-3 text-zinc-500" />
                  <span className="sc-heading font-bold text-[color:var(--sc-pink)]">@{r.target_username}</span>
                  <span className="chip-neon pink text-[9px]">{r.kind}</span>
                  {r.channel_username && <span className="chip-neon volt text-[9px]">#{r.channel_username}</span>}
                </div>
                <div className="text-[10px] text-zinc-500 sc-mono mt-1">{r.created_at?.slice(0, 19).replace("T", " ")} · reporter IP: {r.reporter_ip || "?"}</div>
              </div>
            </div>
            <div className="text-sm text-zinc-200 leading-relaxed border-l-2 border-[color:var(--sc-orange)] pl-3 py-1 my-2">{r.reason}</div>
            {r.notes && <div className="text-xs text-zinc-400 italic">Notes: {r.notes}</div>}
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => setReportStatus(r.id, "reviewing")} className="sc-btn-ghost text-[10px]" data-testid={`report-reviewing-${r.id}`}>Mark reviewing</button>
              <button onClick={() => {
                const notes = prompt("Notes for action taken:") || "";
                setReportStatus(r.id, "actioned", notes);
              }} className="sc-btn text-[10px]" data-testid={`report-actioned-${r.id}`}>
                <Check className="w-3 h-3 inline mr-1" /> Actioned
              </button>
              <button onClick={() => {
                const notes = prompt("Reason for dismissal:") || "";
                setReportStatus(r.id, "dismissed", notes);
              }} className="sc-btn-ghost text-[10px]" data-testid={`report-dismissed-${r.id}`}>
                <X className="w-3 h-3 inline mr-1" /> Dismiss
              </button>
              <Link to={`/u/${r.target_username}`} className="sc-btn-ghost text-[10px]">View target</Link>
              {r.channel_username && <Link to={`/c/${r.channel_username}`} className="sc-btn-ghost text-[10px]">Open channel</Link>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// IP BANS TAB
// ============================================================
function IPBansTab() {
  const [bans, setBans] = useState([]);
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/ip-bans");
    setBans(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!ip.trim()) return toast.error("Enter an IP");
    setBusy(true);
    try {
      await api.post("/admin/ip-bans", { ip: ip.trim(), reason });
      toast.success(`Banned IP ${ip.trim()}`);
      setIp(""); setReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (ip) => {
    if (!confirm(`Unban IP ${ip}?`)) return;
    try {
      await api.delete(`/admin/ip-bans/${encodeURIComponent(ip)}`);
      toast.success("Unbanned");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_minmax(0,1fr)] gap-4" data-testid="ipbans-tab">
      <form onSubmit={add} className="sc-card p-6 space-y-3">
        <div className="sc-heading text-lg font-bold flex items-center gap-2">
          <PlusCircle className="w-4 h-4 text-[color:var(--sc-pink)]" /> New IP Ban
        </div>
        <p className="text-xs text-zinc-400">Reject WebSocket connections from this IP. IPv4 or IPv6. Currently-connected sockets are dropped.</p>
        <div>
          <label className="sc-label block mb-2">IP address</label>
          <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="192.0.2.1" className="sc-input sc-mono" data-testid="ipban-ip" required />
        </div>
        <div>
          <label className="sc-label block mb-2">Reason</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} className="sc-input resize-none" placeholder="Ban evasion, spam network, etc." data-testid="ipban-reason" />
        </div>
        <button disabled={busy} type="submit" className="sc-btn text-xs" data-testid="ipban-submit">Ban IP</button>
      </form>

      <div className="sc-card p-6">
        <div className="sc-heading text-lg font-bold mb-3">Active Bans ({bans.length})</div>
        <div className="max-h-[600px] overflow-y-auto space-y-2">
          {bans.length === 0 && <div className="text-xs text-zinc-500 py-4 text-center">No IPs banned.</div>}
          {bans.map((b) => (
            <div key={b.ip} className="rounded border border-[color:var(--sc-pink)]/30 p-3" data-testid={`ipban-row-${b.ip}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="sc-mono text-sm text-white">{b.ip}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">by {b.banned_by} · {b.created_at?.slice(0,16).replace("T"," ")}</div>
                  {b.reason && <div className="text-xs text-zinc-300 mt-1">{b.reason}</div>}
                </div>
                <button onClick={() => remove(b.ip)} className="sc-btn-ghost text-xs" data-testid={`ipban-remove-${b.ip}`}>
                  <RotateCcw className="w-3 h-3 inline mr-1" /> Unban
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LIVE MONITOR TAB — see every live stream + drop into chat
// ============================================================
function LiveMonitorTab() {
  const [data, setData] = useState({ channels: [], total_live: 0, total_ws_viewers: 0 });
  const [selectedChan, setSelectedChan] = useState(null);
  const [chatLog, setChatLog] = useState([]);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/live"); setData(r.data); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const openChat = async (channel) => {
    setSelectedChan(channel);
    try { const r = await api.get(`/admin/channel-chat/${channel.username}?limit=200`); setChatLog(r.data); }
    catch { setChatLog([]); }
  };

  return (
    <div className="space-y-4" data-testid="live-monitor-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Live channels" value={data.total_live} tone="pink" />
        <StatTile label="WS viewers" value={data.total_ws_viewers} tone="volt" />
        <StatTile label="Reports (open)" value="—" tone="orange" note="check Reports tab" />
        <StatTile label="Refresh" value="8s" tone="cyan" note="auto" />
      </div>

      <div className="grid lg:grid-cols-[380px_minmax(0,1fr)] gap-4">
        <div className="sc-card p-4">
          <div className="sc-heading text-lg font-bold mb-3 flex items-center gap-2">
            <Radio className="w-4 h-4 text-[color:var(--sc-pink)]" /> Live Now
          </div>
          <div className="max-h-[600px] overflow-y-auto space-y-2">
            {data.channels.length === 0 && <div className="text-xs text-zinc-500 py-6 text-center">No live streams right now.</div>}
            {data.channels.map((c) => (
              <button
                key={c.username}
                onClick={() => openChat(c)}
                className={`w-full text-left p-3 rounded border transition ${
                  selectedChan?.username === c.username
                    ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10"
                    : "border-white/5 hover:border-white/20"
                }`}
                data-testid={`live-channel-${c.username}`}
              >
                <div className="flex items-center justify-between">
                  <div className="sc-heading font-bold text-sm truncate">@{c.username}</div>
                  <span className="chip-neon pink text-[9px]"><Eye className="w-2.5 h-2.5" /> {c.ws_viewers}</span>
                </div>
                <div className="text-xs text-zinc-400 truncate mt-0.5">{c.title}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{c.category} {c.game_slug && `· ${c.game_slug}`}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="sc-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="sc-heading text-lg font-bold flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[color:var(--sc-cyan)]" />
              {selectedChan ? `Chat · @${selectedChan.username}` : "Chat"}
            </div>
            {selectedChan && (
              <Link to={`/c/${selectedChan.username}`} className="sc-btn-ghost text-xs flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Open channel
              </Link>
            )}
          </div>
          {!selectedChan ? (
            <div className="text-xs text-zinc-500 py-8 text-center">Select a live stream to view its chat log.</div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto space-y-1 font-mono text-xs">
              {chatLog.length === 0 && <div className="text-xs text-zinc-500 py-6 text-center">No chat activity yet.</div>}
              {chatLog.map((m, i) => (
                <div key={m.id || i} className="flex gap-2 py-0.5">
                  <span className="text-zinc-600">{(m.created_at || "").slice(11, 19)}</span>
                  <span className="text-[color:var(--sc-volt)] font-bold">@{m.username || "?"}</span>
                  <span className="text-zinc-200 break-words">{m.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone, note }) {
  return (
    <div className="sc-card p-4">
      <div className="sc-label text-[9px]" style={{ color: `var(--sc-${tone})` }}>{label}</div>
      <div className="text-3xl sc-heading font-black text-white mt-1">{value}</div>
      {note && <div className="text-[10px] text-zinc-500 mt-1">{note}</div>}
    </div>
  );
}
